import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import {
  listSubitemDiscrepancyParents,
  scanSubitemDiscrepancies,
  scanSubitemDiscrepancyParent,
} from "../../utils/requests/subitemDiscrepancyRequests";
import type { SubitemScanParent } from "../../components/Resynchronization/__fixtures__/discrepancyFixtures";

/**
 * Parents per scan request. The same bound the Subitems tab's per-page scan has always used
 * (`limit`, 25): SUBITEM_BUDGET_PER_PARENT is 500, so a window is at most ~12,500 subitems, which
 * is the size the engine's async scan was measured against. The page passes its own `limit` so
 * the two bounds cannot drift apart.
 */
export const WALK_WINDOW_SIZE = 25;

export type SubitemWalkStatus = "idle" | "listing" | "scanning" | "done" | "error";

export interface SubitemDiscrepancyWalk {
  /**
   * `idle` until the walk is enabled; `listing` while the parent list is being fetched;
   * `scanning` while windows are still being scanned; `done` once every parent has been asked
   * about; `error` when either the listing or a window failed (see `retry`).
   */
  status: SubitemWalkStatus;
  error: string | null;
  /** Parents whose scan PROVED subitem drift, in the board's listing order (by item name). */
  matching: SubitemScanParent[];
  /** Every parent scanned so far, by sync id — drifting, clean, deferred and unverified alike. */
  parents: ReadonlyMap<string, SubitemScanParent>;
  /** Parents asked about so far, including those the engine answered nothing for. */
  scannedCount: number;
  /** Parents on the board. 0 until the listing resolves. */
  totalCount: number;
  /**
   * Parents the walk could not reach a verdict on: `deferred` (over the per-parent budget, scanned
   * only on demand) or `unverified` (a board the viewer cannot see). They are NOT in `matching`,
   * so a reader must be told they exist — a drifting parent among them is invisible here.
   */
  skippedCount: number;
  /** Clears a window's error and resumes from that window; re-lists after a listing error. */
  retry: () => void;
  /** Re-lists the board and scans it again from the start. */
  restart: () => void;
  /** Scans one parent and merges the fresh verdict — after a write, or for a deferred parent. */
  rescanParent: (parentSyncId: string) => Promise<SubitemScanParent>;
}

interface Listing {
  parentSyncIds: string[];
  /**
   * When this listing was fetched. Present so two listings are never deep-equal: SWR keeps the
   * OLD data reference when a refetch returns structurally equal data, and the walk restarts on
   * the listing's identity. Without this, the bulk write's invalidation (below) would re-list an
   * unchanged board and the walk would not notice.
   */
  listedAt: number;
}

/**
 * The Subitems tab's board-wide, progressive scan — the thing behind its Discrepancies filter.
 *
 * WHY IT EXISTS. The per-page scan is bounded to `limit` parents (it has to be: the scan is a
 * Monday read per parent, and unbounded it 504'd). Filtering "parents with subitem drift" INSIDE
 * that window is what made the filtered list page unevenly — three rows, then none, then one —
 * because each page was only ever "whichever of these 25 happen to drift". Answering the filter
 * honestly means asking about every parent, which is what the Items tab's own Discrepancies mode
 * does (`useBoardView` streams and accumulates the whole board). This is that model for
 * subitems: list every parent, scan them a window at a time, accumulate, and let the tab page the
 * drifting set.
 *
 * SEQUENTIAL, one window in flight at a time. Each window is already fetched concurrently inside
 * the engine, and the page scan has no account lock (see scan-job.cache.ts) — running windows in
 * parallel here would multiply a spend nothing is pacing.
 *
 * OWNED BY THE PAGE, not the tab, for the same reason `useSubitemBulkScan` is: Vibe's TabPanels
 * unmounts the inactive tab, and a walk that restarted on every tab switch would redo minutes of
 * scanning to answer a question it had already answered.
 *
 * PAUSES rather than discards when disabled (the filter switched back to All): the results and
 * the cursor are kept, so switching back resumes where it left off.
 */
const useSubitemDiscrepancyWalk = ({
  currentBoard,
  token,
  enabled,
  windowSize = WALK_WINDOW_SIZE,
}: {
  currentBoard: number | null;
  token: string | null;
  enabled: boolean;
  windowSize?: number;
}): SubitemDiscrepancyWalk => {
  // Once asked for, the listing stays keyed — so disabling pauses the walk instead of nulling the
  // key, which would drop the listing and, with it, everything scanned against it.
  const [everEnabled, setEverEnabled] = useState(enabled);
  useEffect(() => {
    if (enabled) setEverEnabled(true);
  }, [enabled]);

  /*
   * Under the `subitemDiscrepancy:` prefix ON PURPOSE. `useSubitemBulkScan` invalidates every key
   * with that prefix once a board-wide write completes, because every cached subitem verdict is
   * stale after one. That invalidation re-fetches this listing, the fresh listing has a new
   * identity (see `Listing.listedAt`), and the effect below restarts the walk — so the tab's
   * filtered list corrects itself after a bulk write without the page having to thread the bulk
   * job's state into it.
   */
  const listingKey =
    currentBoard && token && everEnabled ? `subitemDiscrepancy:${currentBoard}:parents` : null;

  const {
    data: listing,
    error: listingError,
    mutate: relist,
  } = useSWR<Listing>(
    listingKey,
    async () => {
      const response = await listSubitemDiscrepancyParents(token!, String(currentBoard));
      return { parentSyncIds: response?.parentSyncIds ?? [], listedAt: Date.now() };
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      // A listing is re-fetched only by `restart`/`retry` or the bulk write's invalidation. An
      // automatic revalidation would restart a walk that was fine.
      revalidateIfStale: false,
      shouldRetryOnError: false,
    },
  );

  const [results, setResults] = useState<Map<string, SubitemScanParent>>(() => new Map());
  const [cursor, setCursor] = useState(0);
  const [walkError, setWalkError] = useState<string | null>(null);
  const inFlightRef = useRef(false);
  /*
   * Bumped whenever the walk restarts, and captured by every request before it is sent. A window
   * or single-parent scan that resolves after a restart belongs to the OLD listing; merging it
   * would put verdicts from one walk into another, and clearing `inFlightRef` from it would let
   * two windows run at once.
   */
  const generationRef = useRef(0);

  // A fresh listing is a fresh walk — including one forced by the invalidation described above.
  useEffect(() => {
    generationRef.current += 1;
    inFlightRef.current = false;
    setResults(new Map());
    setCursor(0);
    setWalkError(null);
  }, [listing]);

  const ids = listing?.parentSyncIds;

  useEffect(() => {
    if (!enabled || !ids || !token || !currentBoard) return;
    if (walkError || inFlightRef.current || cursor >= ids.length) return;

    const window = ids.slice(cursor, cursor + windowSize);
    const generation = generationRef.current;
    inFlightRef.current = true;

    // The flag is cleared INSIDE each handler, before its state updates, never in a `finally`.
    // A `finally` runs a microtask after `then`, and React may flush the state update — and
    // re-run this effect — between the two; the effect would then see the flag still set, start
    // nothing, and have no later change to wake it. The walk would stop one window in.
    scanSubitemDiscrepancies(token, String(currentBoard), window)
      .then((response) => {
        if (generation !== generationRef.current) return;
        inFlightRef.current = false;
        setResults((previous) => {
          const next = new Map(previous);
          (response?.parents ?? []).forEach((parent) => next.set(parent.parentSyncId, parent));
          return next;
        });
        // The window's length, not `windowSize`: the last window is short.
        setCursor(cursor + window.length);
      })
      .catch((error: any) => {
        if (generation !== generationRef.current) return;
        inFlightRef.current = false;
        // The cursor stays on this window, so `retry` resumes here rather than from the start.
        setWalkError(error?.message ?? "The scan did not complete.");
      });
  }, [enabled, ids, cursor, walkError, token, currentBoard, windowSize]);

  const matching = useMemo(
    () =>
      (ids ?? []).flatMap((id) => {
        const parent = results.get(id);
        // Same rule as the per-page filter it replaces: only a PROVEN drift belongs. A deferred
        // or unverified parent is counted in `skippedCount` instead, never shown as clean.
        return parent && parent.status === "scanned" && parent.totalDiscrepancies > 0
          ? [parent]
          : [];
      }),
    [ids, results],
  );

  const skippedCount = useMemo(() => {
    let count = 0;
    results.forEach((parent) => {
      if (parent.status !== "scanned") count += 1;
    });
    return count;
  }, [results]);

  const totalCount = ids?.length ?? 0;
  const scannedCount = Math.min(cursor, totalCount);

  const error: string | null = listingError
    ? (listingError?.message ?? "The board's items could not be listed.")
    : walkError;

  const status: SubitemWalkStatus = !enabled
    ? "idle"
    : error
      ? "error"
      : !ids
        ? "listing"
        : cursor < ids.length
          ? "scanning"
          : "done";

  const retry = useCallback(() => {
    if (listingError) {
      void relist();
      return;
    }
    setWalkError(null);
  }, [listingError, relist]);

  const restart = useCallback(() => {
    void relist();
  }, [relist]);

  const rescanParent = useCallback(
    async (parentSyncId: string): Promise<SubitemScanParent> => {
      // Same envelope contract `useSubitemDiscrepancy.scanParent` documents: `/scan-parent`
      // answers `{ parents: [one] }`, looked up by the id that was REQUESTED so a shape change
      // throws rather than silently merging nothing.
      const response = await scanSubitemDiscrepancyParent(
        token!,
        String(currentBoard),
        parentSyncId,
      );
      const scanned = response?.parents?.find((parent) => parent.parentSyncId === parentSyncId);
      if (!scanned) {
        throw new Error(`scan-parent returned no result for ${parentSyncId}`);
      }
      // No generation check: a fresh single-parent verdict is correct for whichever walk is
      // current, and a parent the new listing does not carry simply never reaches `matching`.
      setResults((previous) => new Map(previous).set(parentSyncId, scanned));
      return scanned;
    },
    [token, currentBoard],
  );

  return {
    status,
    error,
    matching,
    parents: results,
    scannedCount,
    totalCount,
    skippedCount,
    retry,
    restart,
    rescanParent,
  };
};

export default useSubitemDiscrepancyWalk;
